import React, { useContext, useState } from 'react';
import { Checkbox, FormControlLabel, Hidden } from '@material-ui/core';
import Loader from '@cpg-its/ads.common.loading-circle';
import Banner from '@cpg-its/ads.containers.banner';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { getOrganization } from '../../../../global/ducks/organization-prc-api';
import { dispatchResetInstitutionClientNumber, dispatchSetCurrentInstitution, setInstitutionClientNumber, setInstitutionGuid } from '../institutions/institution-slice';
import { SearchApp } from '../../../../search/components/search-app';
import { SearchList } from '../../../../search/components/search-list';
import { InstitutionItem, InstitutionState } from '../institutions/institution-model';
import { InstitutionProps, SearchProps } from '../../models/arc-epi-props';
import { SearchBar } from '../../../../search/components/search-bar';
import { ListPagination } from '../../../../search/components/list-pagination';
import { InstitutionItemView } from './institution-item-view';
import { InstitutionListLabels } from './institution-list-labels';
import { PageHeader } from '../../../../global/components/layout/page-header';
import { TcsmStates } from '../../../../root/tcsm-model';
import { sortTableStyles } from '../../../../global/constants/theme/styles/tables/generic-sort-table';
import { usePersonManagementReset } from '../../hooks/usePersonManagementReset';
import { SideBarContent } from '../../../../global/components/layout/sidebar-content';
import { useGpsType } from '../../../../global/hooks/useGpsType';
import { GpsType } from '../../../../global/components/layout/layout-models';
import { GuidedProcessSupportBlock } from '../../../../global/components/guided-process-support-block';
import { setNavigationContext } from '../../../../global/ducks/app-context';
import { NavigationContext } from '../../../../global/models/app-context-model';
import { InstitutionContext, InstitutionContextKeys } from '../../../../global/models/institution-model';
import { useManageInstitutionList } from '../../hooks/useManageInstitutionList';
import { useQuery } from '../../../../global/hooks/useQuery';
import { ContentSpacer } from '../../../../global/components/layout/generic-content-spacer';
import { WysiwygWithReactLink } from '../../../../global/components/wysiwyg-with-react-link';
import { buttonStyles } from '../../../../global/constants/theme/styles/components/buttonstyles';
import { useBasePath } from '../../../../global/hooks/useBasePath';
import { ArcRoutes } from '../../constants/routes';
import { ButtonWrapper } from '../../../../global/components/layout/button-wrapper';

const addNewModeKey = 'add';
function InstitutionSearch() {

	const context = useContext(InstitutionContext);
	const institutionProps = useSelector<TcsmStates, InstitutionProps>(state => state?.epiProps?.institutionSettingsState?.epiContent);
	const institutionState = useSelector<TcsmStates, InstitutionState>(state => state.institutionState);
	const initialList = useSelector<TcsmStates, InstitutionItem[]>(state => context === InstitutionContextKeys.manageInstitution ? state.institutionState.manageInstitutionList : state.institutionState.institutionList);
	const searchSettings = useSelector<TcsmStates, SearchProps>(state => state.epiProps.searchSettingsState.epiContent);
	const addNewMode = useQuery()?.get('mode') === addNewModeKey;
	const { state } = useLocation<any>();
	const [checked, setChecked] = useState(false);
	const history = useHistory();
	const dispatch = useDispatch();
	const tableClasses = sortTableStyles();
	const gpsItem = useGpsType(GpsType.INSTITUTION_LIST);
	const buttonClass = buttonStyles();
	const addNewInstitutionLink = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.InstitutionRoot}${ArcRoutes.InstitutionAddRequest}`);
	usePersonManagementReset();

	const onSet = (institutionId, guid) => {
		dispatch(setInstitutionClientNumber(institutionId));
		dispatch(setInstitutionGuid(guid));
		dispatch(setNavigationContext(NavigationContext.Institution));
		dispatch(getOrganization(institutionId, dispatchSetCurrentInstitution(dispatch), dispatchResetInstitutionClientNumber(dispatch)));
		history.push(state.actionLink, state);
	};

	if (context === InstitutionContextKeys.manageInstitution) {
		useManageInstitutionList();
	}


	if (context === InstitutionContextKeys.manageInstitution ? !institutionState.manageInstitutionListLoaded : !institutionState.institutionListLoaded) {
		return (<Loader />);
	}

	// Autoselect the only item.
	if (initialList?.length === 1) {
		const item = initialList[0];
		onSet((item as InstitutionItem).client_number, (item as InstitutionItem).guid);
		return (<Loader />);
	}

	return (
		<>
			<PageHeader
				headerText={addNewMode ? institutionProps?.institutionAddRequestLandingPageTitle : searchSettings?.institutionSearchPageTitle}
			/>
			{
				addNewMode &&
				<ContentSpacer>
					<Banner
						banner={{
							type: 'warning',
							label: <WysiwygWithReactLink content={institutionProps?.institutionAddRequestLandingPageWarning} />,
						}}
					/>
				</ContentSpacer>

			}
			<Hidden mdUp>
				<SideBarContent>
					<GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
				</SideBarContent>
			</Hidden>
			{
				addNewMode &&
				<ContentSpacer>
					<p><strong>{institutionProps?.institutionAddRequestStepOneTitle}</strong></p>
				</ContentSpacer>

			}
			<ContentSpacer>

				<SearchApp initialList={initialList} appName={`InstitutionSearch-${context}`}>
					{
						initialList?.length >= 10 && <SearchBar label={searchSettings.searchBarLabel} name='institution-search-bar' filterProps={['client_number', 'legal_name', 'city']} />

					}
					<div className={tableClasses.sortTable}>
						<Hidden xsDown>
							<InstitutionListLabels settings={searchSettings} />
						</Hidden>
						<div className='sort-table-content'>
							<SearchList
								renderList={
									(item) => {
										return <InstitutionItemView settings={searchSettings} key={item.id} item={item} action={() => { onSet((item as InstitutionItem).client_number, (item as InstitutionItem).guid); }} />;
									}
								}
								renderPagination={
									(totalCounts, name) => {
										return <ListPagination name={name} totalCounts={totalCounts} totalPages={Math.ceil(totalCounts / 10)} nextButton={searchSettings.nextButton} prevButton={searchSettings.previousButton} />;
									}
								}
								emptyMessage={searchSettings.emptyListMessage}
							/>
						</div>
					</div>
				</SearchApp>
			</ContentSpacer>
			{
				addNewMode &&
				<ContentSpacer>
					<p><strong>{institutionProps?.institutionAddRequestStepTwoTitle}</strong></p>
					<FormControlLabel
						control={
							<Checkbox
								checked={checked}
								onChange={() => setChecked(!checked)}
								name="formEnabled"
							/>
						}
						label={institutionProps?.institutionAddRequestLandingPageCheckBoxMessage}
					/>
					<ButtonWrapper flexEnd>
						<button className={buttonClass.primaryButton} type='button' disabled={!checked} onClick={() => history.push(addNewInstitutionLink)}>{institutionProps?.redirectButtonText}</button>
					</ButtonWrapper>
				</ContentSpacer>

			}
		</>
	);
}

export default InstitutionSearch;